// SKILL-PROPOSALS-OPERATOR (Andrei, 2026-11-01): agents propose skills through one door; the
// queue behind it is the platform operator's alone. Everything about skills is invisible to
// companies: no company route reads a proposal, its draft, its reviewer, or Skillsmith's name.
//
//   the DE (its own running run):
//   POST /v1/companies/{co}/agent/skill-proposals      `propose_skill`: files it; the answer
//                                                       says only "Sent for review"
//   GET  /v1/companies/{co}/agent/skill-proposals      `suggestion_outcomes`: its own proposals'
//                                                       neutral outcomes (pending, accepted,
//                                                       declined), no reviewer, no skill text
//
//   Skillsmith (the company's system agent, its `cosk_sa_` key; or a super admin's session):
//   GET  /v1/operator/system-agents/{company_id}/{membership_id}/skill-proposals
//   GET  …/skill-proposals/{proposal_id}               with the draft and any revised draft
//   POST …/skill-proposals/{proposal_id}/review        a recommendation and maybe a revised
//                                                       draft: the proposal is `ready`
//
//   a platform super admin (the operator console; 404 to anyone else):
//   GET  /v1/operator/skill-proposals                  every granted company's queue
//   GET  /v1/operator/skill-proposals/{company_id}/{proposal_id}
//   POST …/{company_id}/{proposal_id}/decide           approve or reject (the only human gate).
//                                                       Approve installs nothing: it files a
//                                                       `skill_change` entry in the operator-only
//                                                       issue log for the repo pipeline
//   POST …/{company_id}/{proposal_id}/ship             the repo pipeline landed it (refused until
//                                                       the client conversation is settled)
//
//   NIGHTLY-REVIEW (Andrei, 2026-11-01): the client conversation step. Before a change reaches a
//   client's agents the operator talks to the client owner in outcome terms ("your Marketing
//   Director will now ask for the audience before drafting"), never skill internals (rule 7m):
//   POST …/{company_id}/{proposal_id}/consent          needs_client_ok | client_ok |
//                                                       no_consent_needed (with the reason)
//   POST …/{company_id}/{proposal_id}/client-message   drafts the outcome-terms message (saved on
//                                                       the proposal; the operator edits it in
//                                                       the console); nothing is sent
//   POST …/{company_id}/{proposal_id}/client-message/send   the operator sends the edited message
//                                                       to the client owner(s) by mail
//
// The operator writes change no company state and write no company event (an event in the
// company's log would show the company the queue): they are reads for the event rules, audited
// in `cp.platform_audit` instead.
import { z } from "zod";
import {
  SkillProposalConsent,
  SkillProposalRecommendation,
  SkillProposalSource,
  SkillProposalStatus,
} from "../enums";
import { Slug, Timestamp, Uuid } from "../primitives";
import { CompanyParams } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const operatorBase = "/v1/operator";
const agents = { feature: "agents", tag: "runs" } as const;
const operator = { feature: "core", tag: "operator", access: "operator" } as const;

/** What the DE is told when it files one, and nothing more (no reviewer, no queue). */
export const SKILL_PROPOSAL_SENT = "Sent for review";
/** The neutral outcome notes a DE reads back. */
export const SKILL_PROPOSAL_NOTES = {
  pending: "Sent for review",
  accepted: "Your suggestion was accepted",
  declined: "Your suggestion was declined",
} as const;

/** The pack key format: lowercase letters, digits and dashes, at most 63 characters. */
export const SkillProposalName = z
  .string()
  .trim()
  .min(1)
  .max(63)
  .regex(/^[a-z0-9][a-z0-9-]{0,62}$/, "a skill key: lowercase letters, digits and dashes");

export const SkillProposalEvidence = z
  .strictObject({ kind: z.enum(["run", "task", "message"]), id: Uuid })
  .meta({ id: "SkillProposalEvidence" });

/** `propose_skill`: what a DE proposes. */
export const AgentSkillProposal = z
  .strictObject({
    /** The skill's key: an existing skill to change, or a new one. */
    name: SkillProposalName,
    /** Why: what kept going wrong, or what the skill would do better (a few lines). */
    why: z.string().trim().min(1).max(2000),
    /** The runs (tasks, messages) that show it: at least one, at most ten. */
    evidence: z.array(SkillProposalEvidence).min(1).max(10),
    /** The draft SKILL.md, whole (never executed, never installed from here). */
    draft: z.string().trim().min(1).max(100_000),
  })
  .meta({ id: "AgentSkillProposal" });
export type AgentSkillProposal = z.infer<typeof AgentSkillProposal>;

/** The DE's answer: sent, and nothing about who reviews it. */
export const AgentSkillProposed = z
  .object({
    proposal_id: Uuid,
    status: z.literal("sent_for_review"),
    message: z.literal(SKILL_PROPOSAL_SENT),
    step_id: Uuid,
  })
  .meta({ id: "AgentSkillProposed" });

export const AgentSkillOutcome = z
  .object({
    proposal_id: Uuid,
    /** The key it proposed (its own words; never anyone's revision). */
    name: z.string(),
    outcome: z.enum(["pending", "accepted", "declined"]),
    note: z.enum([
      SKILL_PROPOSAL_NOTES.pending,
      SKILL_PROPOSAL_NOTES.accepted,
      SKILL_PROPOSAL_NOTES.declined,
    ]),
    /** First time this outcome is read (an accepted or declined one). */
    new: z.boolean(),
    created_at: Timestamp,
    decided_at: Timestamp.nullable(),
  })
  .meta({ id: "AgentSkillOutcome" });
export type AgentSkillOutcome = z.infer<typeof AgentSkillOutcome>;

export const AgentSkillOutcomes = z
  .object({ data: z.array(AgentSkillOutcome) })
  .meta({ id: "AgentSkillOutcomes" });

const ProposalCompany = z.object({ id: Uuid, slug: Slug, name: z.string() });

/** One proposal in the operator queue (the list leaves the drafts out: `null`). */
export const OperatorSkillProposal = z
  .object({
    company: ProposalCompany,
    id: Uuid,
    name: z.string(),
    why: z.string(),
    evidence: z.array(SkillProposalEvidence),
    draft: z.string().nullable(),
    status: SkillProposalStatus,
    proposer: z.object({ membership_id: Uuid, name: z.string() }),
    run_id: Uuid.nullable(),
    /** Skillsmith's review, once it marked the proposal ready. */
    review: z
      .object({
        recommendation: SkillProposalRecommendation,
        note: z.string().nullable(),
        /** Its revised SKILL.md (the list leaves it out: `null`; see `revised`). */
        draft: z.string().nullable(),
        revised: z.boolean(),
        reviewed_at: Timestamp,
      })
      .nullable(),
    decided_at: Timestamp.nullable(),
    decision_note: z.string().nullable(),
    /** The issue-log entry approval filed for the repo pipeline (`skill_change`). */
    work_item_id: Uuid.nullable(),
    shipped_at: Timestamp.nullable(),
    created_at: Timestamp,
    /** NIGHTLY-REVIEW: who filed it (the DE in a run, or its nightly self-review). */
    source: SkillProposalSource,
    /** What changes, in outcome terms (what the client may hear). */
    outcome: z.string().nullable(),
    /** The KPI effect the proposer expects. */
    expected_effect: z.string().nullable(),
    /** The client conversation: shipping waits for `client_ok` or `no_consent_needed`. */
    consent: z.object({
      state: SkillProposalConsent,
      reason: z.string().nullable(),
      set_at: Timestamp.nullable(),
      /** The outcome-terms message as last drafted or edited (the detail only; the list: null). */
      message_draft: z.string().nullable(),
      message_sent_at: Timestamp.nullable(),
      /** True while shipping is refused for want of the client's yes. */
      blocks_shipping: z.boolean(),
    }),
  })
  .meta({ id: "OperatorSkillProposal" });
export type OperatorSkillProposal = z.infer<typeof OperatorSkillProposal>;

/** NIGHTLY-REVIEW: the operator records where the client conversation stands. */
export const SkillProposalConsentSet = z
  .strictObject({
    state: SkillProposalConsent,
    /** Required for `no_consent_needed` (why: a bug fix, an internal-only DE…); what the client said otherwise. */
    reason: z.string().trim().min(1).max(2000).optional(),
  })
  .meta({ id: "SkillProposalConsentSet" });
export type SkillProposalConsentSet = z.infer<typeof SkillProposalConsentSet>;

/** NIGHTLY-REVIEW: the drafted outcome-terms message, saved on the proposal for the operator to edit. */
export const SkillProposalClientMessage = z
  .object({
    subject: z.string(),
    body: z.string(),
    /** The client owners it would go to (active people with an address). */
    to: z.array(z.object({ membership_id: Uuid, name: z.string(), email: z.string().nullable() })),
    /** Where to edit it before sending (the operator console's proposal page). */
    edit_url: z.string(),
  })
  .meta({ id: "SkillProposalClientMessage" });
export type SkillProposalClientMessage = z.infer<typeof SkillProposalClientMessage>;

/** NIGHTLY-REVIEW: the operator's edited message and recipients: the only way it is sent. */
export const SkillProposalClientMessageSend = z
  .strictObject({
    subject: z.string().trim().min(1).max(200),
    body: z.string().trim().min(1).max(8000),
    /** The people it goes to (active people of the company, by membership id). */
    to: z.array(Uuid).min(1).max(10),
  })
  .meta({ id: "SkillProposalClientMessageSend" });
export type SkillProposalClientMessageSend = z.infer<typeof SkillProposalClientMessageSend>;

export const OperatorSkillProposalList = z
  .object({ data: z.array(OperatorSkillProposal) })
  .meta({ id: "OperatorSkillProposalList" });

export const SkillProposalListQuery = z.object({
  status: SkillProposalStatus.optional().describe("Only proposals in this status"),
});

export const SkillProposalReview = z
  .strictObject({
    recommendation: SkillProposalRecommendation,
    /** Why (for the super admin). */
    note: z.string().trim().min(1).max(2000),
    /** A better SKILL.md than the DE's, whole (optional). */
    revised_draft: z.string().trim().min(1).max(100_000).optional(),
  })
  .meta({ id: "SkillProposalReview" });
export type SkillProposalReview = z.infer<typeof SkillProposalReview>;

export const SkillProposalDecide = z
  .strictObject({
    decision: z.enum(["approve", "reject"]),
    /** The super admin's note (operator-only: the DE is told only accepted or declined). */
    note: z.string().trim().min(1).max(2000).optional(),
  })
  .meta({ id: "SkillProposalDecide" });
export type SkillProposalDecide = z.infer<typeof SkillProposalDecide>;

export const SkillProposalShip = z
  .strictObject({ note: z.string().trim().min(1).max(2000).optional() })
  .meta({ id: "SkillProposalShip" });

const SystemAgentProposalParams = z.object({
  company_id: Uuid,
  membership_id: Uuid,
  proposal_id: Uuid,
});
const OperatorProposalParams = z.object({ company_id: Uuid, proposal_id: Uuid });

export const skillProposalRoutes = {
  "skillProposals.agentPropose": defineRoute({
    ...agents,
    id: "skillProposals.agentPropose",
    method: "post",
    path: `${base}/agent/skill-proposals`,
    summary: "A DE proposes a new or changed skill (sent for review; never installs it)",
    description:
      "A digital employee's own running run (its run token, or its seat-bound agent key with " +
      "`X-CompanyOS-Run`). Files the proposal in the platform's review queue; the answer says " +
      "only that it was sent for review. Refused: a draft with a credential in it, evidence not " +
      "of this company, a second open proposal of the same skill, more than five a day. Logged " +
      "as a step of the run (no name, no text).",
    access: "run_token",
    params: CompanyParams,
    body: AgentSkillProposal,
    idempotency: "optional",
    response: { status: 201, schema: AgentSkillProposed },
    errors: ["run_not_owned", "run_state_conflict", "key_unbound", "conflict"],
    emits: ["run.step_recorded"],
  }),
  "skillProposals.agentOutcomes": defineRoute({
    ...agents,
    id: "skillProposals.agentOutcomes",
    method: "get",
    path: `${base}/agent/skill-proposals`,
    summary:
      "A DE's own proposals and their neutral outcomes (pending, accepted, declined): no reviewer, no skill text",
    access: "run_token",
    params: CompanyParams,
    response: { status: 200, schema: AgentSkillOutcomes },
    errors: ["run_not_owned", "run_state_conflict", "key_unbound"],
    emits: [],
  }),
  "operator.systemAgents.skillProposals.list": defineRoute({
    ...operator,
    id: "operator.systemAgents.skillProposals.list",
    method: "get",
    path: `${operatorBase}/system-agents/{company_id}/{membership_id}/skill-proposals`,
    summary:
      "The company's skill-proposal queue for its system agent's key (`signals:read`) or a super admin",
    params: z.object({ company_id: Uuid, membership_id: Uuid }),
    query: SkillProposalListQuery,
    response: { status: 200, schema: OperatorSkillProposalList },
    errors: ["not_found"],
    emits: [],
  }),
  "operator.systemAgents.skillProposals.get": defineRoute({
    ...operator,
    id: "operator.systemAgents.skillProposals.get",
    method: "get",
    path: `${operatorBase}/system-agents/{company_id}/{membership_id}/skill-proposals/{proposal_id}`,
    summary: "One proposal with the DE's draft and any revised draft",
    params: SystemAgentProposalParams,
    response: { status: 200, schema: OperatorSkillProposal },
    errors: ["not_found"],
    emits: [],
  }),
  "operator.systemAgents.skillProposals.review": defineRoute({
    ...operator,
    id: "operator.systemAgents.skillProposals.review",
    method: "post",
    path: `${operatorBase}/system-agents/{company_id}/{membership_id}/skill-proposals/{proposal_id}/review`,
    summary:
      "The system agent's review (`feedback:write`): a recommendation, a note, maybe a revised draft; the proposal is ready for the super admin",
    params: SystemAgentProposalParams,
    body: SkillProposalReview,
    // Operator-only: no company state, no company event.
    safe: true,
    response: { status: 200, schema: OperatorSkillProposal },
    errors: ["not_found", "conflict", "validation_failed"],
    emits: [],
  }),
  "operator.skillProposals.list": defineRoute({
    ...operator,
    id: "operator.skillProposals.list",
    method: "get",
    path: `${operatorBase}/skill-proposals`,
    summary:
      "Every granted company's skill proposals, newest first (platform super admins only; 404 to anyone else)",
    query: SkillProposalListQuery,
    response: { status: 200, schema: OperatorSkillProposalList },
    errors: ["not_found"],
    emits: [],
  }),
  "operator.skillProposals.get": defineRoute({
    ...operator,
    id: "operator.skillProposals.get",
    method: "get",
    path: `${operatorBase}/skill-proposals/{company_id}/{proposal_id}`,
    summary: "One skill proposal with the DE's draft and the system agent's review",
    params: OperatorProposalParams,
    response: { status: 200, schema: OperatorSkillProposal },
    errors: ["not_found"],
    emits: [],
  }),
  "operator.skillProposals.decide": defineRoute({
    ...operator,
    id: "operator.skillProposals.decide",
    method: "post",
    path: `${operatorBase}/skill-proposals/{company_id}/{proposal_id}/decide`,
    summary:
      "A platform super admin approves or rejects a skill proposal (the only human gate; a repeat returns the first decision)",
    description:
      "Approve installs nothing: it files a `skill_change` entry in the operator-only issue log " +
      "for the repo pipeline, and the proposal waits there until it is shipped. The DE is told " +
      "only that its suggestion was accepted or declined. Audited in `cp.platform_audit`.",
    params: OperatorProposalParams,
    body: SkillProposalDecide,
    safe: true,
    response: { status: 200, schema: OperatorSkillProposal },
    errors: ["not_found", "conflict", "validation_failed"],
    emits: [],
  }),
  "operator.skillProposals.ship": defineRoute({
    ...operator,
    id: "operator.skillProposals.ship",
    method: "post",
    path: `${operatorBase}/skill-proposals/{company_id}/{proposal_id}/ship`,
    summary:
      "Mark an approved skill proposal shipped (the repo pipeline landed it); refused until the client conversation is client_ok or no_consent_needed",
    params: OperatorProposalParams,
    body: SkillProposalShip,
    safe: true,
    response: { status: 200, schema: OperatorSkillProposal },
    errors: ["not_found", "conflict", "validation_failed"],
    emits: [],
  }),
  "operator.skillProposals.consent": defineRoute({
    ...operator,
    id: "operator.skillProposals.consent",
    method: "post",
    path: `${operatorBase}/skill-proposals/{company_id}/{proposal_id}/consent`,
    summary:
      "Record the client conversation's state on a proposal: needs_client_ok, client_ok, or no_consent_needed with the reason",
    description:
      "A change that affects a client's agents ships only once the client owner agreed in " +
      "outcome terms, or the operator recorded why no consent is needed (a bug fix, an " +
      "internal-only DE). Audited in `cp.platform_audit`; nothing is sent to the client here.",
    params: OperatorProposalParams,
    body: SkillProposalConsentSet,
    safe: true,
    response: { status: 200, schema: OperatorSkillProposal },
    errors: ["not_found", "conflict", "validation_failed"],
    emits: [],
  }),
  "operator.skillProposals.clientMessage.draft": defineRoute({
    ...operator,
    id: "operator.skillProposals.clientMessage.draft",
    method: "post",
    path: `${operatorBase}/skill-proposals/{company_id}/{proposal_id}/client-message`,
    summary:
      "Draft the outcome-terms message to the client owner (saved on the proposal to edit; nothing is sent)",
    description:
      "The draft names the digital employee and what will change for the client, in outcome " +
      "terms; it never quotes the skill, the draft SKILL.md, the reviewer or the evidence " +
      "(rule 7m: skill content is confidential). The operator edits it in the console and sends " +
      "it with `…/client-message/send`.",
    params: OperatorProposalParams,
    body: z.strictObject({}),
    safe: true,
    response: { status: 200, schema: SkillProposalClientMessage },
    errors: ["not_found", "conflict"],
    emits: [],
  }),
  "operator.skillProposals.clientMessage.send": defineRoute({
    ...operator,
    id: "operator.skillProposals.clientMessage.send",
    method: "post",
    path: `${operatorBase}/skill-proposals/{company_id}/{proposal_id}/client-message/send`,
    summary:
      "Send the edited outcome-terms message to the client owner(s) by mail (the operator's click; nothing auto-sends)",
    params: OperatorProposalParams,
    body: SkillProposalClientMessageSend,
    safe: true,
    response: { status: 200, schema: OperatorSkillProposal },
    errors: ["not_found", "conflict", "validation_failed", "vendor_unavailable"],
    emits: [],
  }),
};
